/**
 * Battle-state types — the battlescape state model (v0M3.3).
 *
 * Implements `BATTLES.md` §2.7 five-phase engagement structure at the
 * type level. A `Battle` holds a battlescape grid (§2.7.1 20×20), two
 * sides of cohorts, per-cohort facing + stats + tags + figures, plus
 * a battle-scoped round counter and PRNG that's independent of the
 * world tick (per Q1 lock — battle clock decoupled from world).
 *
 * Design notes:
 * - Every field is JSON-serialisable (no functions / Symbols / cycles)
 *   so saves round-trip cleanly per `state.ts` invariant.
 * - Cohort tags are a plain `readonly string[]` (not a Set) for
 *   serialisation. Use `hasTag()` from `cohort.ts` to query.
 * - Facing is a cardinal enum ('N' | 'S' | 'E' | 'W') matching the
 *   4-cardinal movement rule locked at §2.7.3 + `BATTLES.md` §2.7.8.4.b.
 * - Per-figure stats: only `health` is per-figure. Every other combat
 *   stat is cohort-level (attack, defence, armor, stamina, morale, etc.)
 *   per §2.7.8.1-.7.
 * - `Battle` lives on `GameState.battles` (see state.ts extension) and
 *   is stepped each world tick by `battlesSystem` (see battles.ts) at
 *   `BATTLES_ROUNDS_PER_TICK` battle rounds per world tick.
 */

import type { RngState } from './rng';
import type { ArmyId, CohortId, CommanderId, FactionId } from './state';

// ---------------------------------------------------------------------------
// Branded identifier for Battles (added to the state.ts brand family)
// ---------------------------------------------------------------------------

export type BattleId = number & { readonly __brand: 'BattleId' };
export const toBattleId = (n: number): BattleId => n as BattleId;

// ---------------------------------------------------------------------------
// Battlescape geometry
// ---------------------------------------------------------------------------

/** Battlescape grid width per BATTLES.md §2.7.1. */
export const BATTLESCAPE_WIDTH = 20;
/** Battlescape grid height per BATTLES.md §2.7.1. */
export const BATTLESCAPE_HEIGHT = 20;

/** Battle rounds per world tick — dial (post-MVP tuning candidate). */
export const BATTLES_ROUNDS_PER_TICK = 1;

/**
 * Legacy infantry front-rank width. **Session-27b (2026-07-24):** retired
 * from runtime consumption in favour of per-cohort `CohortStats.frontWidth`
 * (xlsx-authoritative via `Balance.xlsx` Cohorts sheet column O). Kept as
 * an exported constant only for test-fixture convenience — real cohort
 * dimensions come from the xlsx spec.
 */
export const COHORT_WIDTH = 12;
/** Legacy infantry rank depth. Session-27b: retired from runtime consumption
 *  (depth is now derived as `figureCount / frontWidth` per cohort). Kept
 *  for test-fixture convenience only. */
export const COHORT_DEPTH = 5;
/** Legacy infantry cohort figure count (12×5 = 60). Session-27b: retired
 *  from runtime consumption in favour of `CohortSpec.figureCount`. */
export const COHORT_SIZE = COHORT_WIDTH * COHORT_DEPTH;
/** Legacy Siege cohort figure count. Session-27b: retired from runtime
 *  consumption in favour of `CohortSpec.figureCount`. */
export const SIEGE_COHORT_SIZE = 4;

/** Cardinal facing direction — 4-cardinal per §2.7.3 amendment. */
export type Facing = 'N' | 'S' | 'E' | 'W';

/** Grid square coordinate. */
export interface Square {
  readonly x: number;
  readonly y: number;
}

// ---------------------------------------------------------------------------
// Battlescape terrain (v0M4.0j — BATTLES.md §2.8.6)
// ---------------------------------------------------------------------------

/**
 * Battlescape terrain type — 10-type catalogue per BATTLES.md §2.8.6.1
 * (v0M4.0k rename + additions).
 *
 * Serialised to `Battle.terrain: Uint8Array` via `TERRAIN_TYPE_ORDER`
 * ordinals so the sim carries one byte per cell.
 *
 * v0M4.0k (2026-07-27) — renames + additions:
 *   - Grass → Plains (matches Combat.xlsx label convention)
 *   - Marsh → Swamp
 *   - Fortification → Wall (renamed; enemies cannot enter)
 *   - Desert (new — battlefield base for Desert strategic-map cells;
 *     generator emission deferred, safe fallback = Plains)
 *   - Gate (new — enemy-passable breach point in a Wall perimeter)
 */
export type TerrainType =
  | 'Plains'
  | 'Hills'
  | 'Forest'
  | 'Swamp'
  | 'Water'
  | 'Mountains'
  | 'Bridge'
  | 'Wall'
  | 'Desert'
  | 'Gate';

/**
 * Canonical ordinal → TerrainType mapping. `Battle.terrain[i]` stores
 * the index into this array. Ordinals are FROZEN for existing types
 * (renames preserve ordinal position); new types append at the end.
 */
export const TERRAIN_TYPE_ORDER: readonly TerrainType[] = [
  'Plains',      // ordinal 0 (was Grass)
  'Hills',       // ordinal 1
  'Forest',      // ordinal 2
  'Swamp',       // ordinal 3 (was Marsh)
  'Water',       // ordinal 4
  'Mountains',   // ordinal 5
  'Bridge',      // ordinal 6
  'Wall',        // ordinal 7 (was Fortification)
  'Desert',      // ordinal 8 (new)
  'Gate',        // ordinal 9 (new)
];

/** Total battlescape cell count (20 × 20 = 400). */
export const BATTLESCAPE_CELL_COUNT = BATTLESCAPE_WIDTH * BATTLESCAPE_HEIGHT;

/**
 * Battlefield feature — a named cluster of cells with a semantic role.
 * Rendered as a distinct overlay on top of the base terrain layer;
 * consumed by playtest-audit tools and by the modal renderer to place
 * feature-specific glyphs (bridge planks, hill triangles, etc.).
 */
export interface TerrainFeature {
  /**
   * Feature kind. `Rubble` (v0M4.2.4) marks cells that were formerly
   * siege-battle Wall but have been breached; the underlying terrain
   * is now Plains with a rubble-pile overlay glyph and no cover bonus.
   * `Gate` (v0M4.0k) marks cells acting as enemy-passable breach points
   * in a Wall perimeter; underlying terrain is Gate.
   */
  readonly type: 'Bridge' | 'Wall' | 'Gate' | 'ForestPatch' | 'HillsCluster' | 'RiverStrip' | 'Rubble';
  /** Cells this feature spans. Sorted (y then x) for deterministic serialisation. */
  readonly cells: readonly (readonly [number, number])[];
}

// ---------------------------------------------------------------------------
// Combat stat block — cohort-level per §2.7.8.1-.7
// ---------------------------------------------------------------------------

/**
 * Cohort combat stats. Every value is a base — modifiers (charge, flank,
 * rear, stamina drain, etc.) apply at strike time per §2.7.8.1-.7 and
 * do NOT mutate these fields. Stamina / morale ARE mutated (they're the
 * running battle-scoped values).
 *
 * Balance authoring per `BATTLES.md` §2.7.8.11 preamble — every dial
 * lives in `Balance.xlsx`; the values here are constants set at cohort
 * creation from the xlsx data pipeline (`src/data/dials.ts`).
 */
export interface CohortStats {
  readonly attack: number;
  readonly defence: number;
  readonly armor: number;
  readonly initiative: number;
  /** Cohort-level charge bonus (0 = cannot charge). §2.7.8.3.a condition 1. */
  readonly chargeBonus: number;
  /** Missile Attack Rating — used if cohort tagged `Ranged`. §2.7.8.2.a. */
  readonly missileAttack: number;
  /** Missile Attack Range in battlescape squares (Chebyshev). §2.7.8.2.b. */
  readonly missileRange: number;
  /** Per-figure max health. §2.7.8.1.b sub-phase 4. */
  readonly maxHealthPerFigure: number;
  /**
   * v0M4.0j.2 — Battlefield movement rate in squares per round per
   * BATTLES.md §2.8.6.3. Default 1 (all non-cavalry). Cavalry = 3.
   * Clamped to 1 when the cohort stands on Hills/Marsh/Forest terrain.
   * Sourced from `CohortSpec.moveSpeedCombat` in Balance.xlsx.
   */
  readonly moveSpeedCombat: number;
  /**
   * Session-27b (2026-07-24) — Front-rank width in figures. Sourced from
   * `CohortSpec.frontWidth` (`Balance.xlsx Cohorts` col O). Retires the
   * global `COHORT_WIDTH` constant for runtime consumption; renderer +
   * `cohort.ts::healthyFrontRowCount` now read per cohort. Depth is
   * derived as `figureCount / frontWidth`.
   */
  readonly frontWidth: number;
  /**
   * Session-27b (2026-07-24) — Total figures at cohort creation. Sourced
   * from `CohortSpec.figureCount` (`Balance.xlsx Cohorts` col N). The
   * living-figure count varies over battle via casualties; `figureCount`
   * here is the roster template used to size the grid render.
   */
  readonly figureCount: number;
}

/**
 * Standing order per `BATTLES.md` §2.8.1 (v0M4.0a lock, 2026-07-14).
 *
 * Tag-conditional catalogue:
 * - **Universal** (all cohorts): `withdraw-with-haste`.
 * - **Melee-only** (cohorts with `Melee` tag): `advance-and-attack` (default),
 *   `hold-and-defend`, `hold-and-delay`, `flank-melee`, `flank-ranged`.
 * - **Ranged-only** (cohorts with `Ranged` tag): `fire-at-will` (default for
 *   ranged), `aggressive-fire`, `hold-fire`.
 *
 * `advance-and-attack` = renamed from pre-v0M4.0a `'attack'`.
 * `hold-and-defend` = renamed from pre-v0M4.0a `'defend-only'`.
 *
 * v0M4.0b.1 lands the enum + rename with NO behaviour change. New variants
 * fall through to `advance-and-attack` semantics in the phase code (marked
 * `PLAYTEST OPEN` in-line); real semantics land at v0M4.0b.2 (melee-side)
 * and v0M4.0b.3 (ranged-side).
 */
export type StandingOrder =
  | 'advance-and-attack'
  | 'hold-and-defend'
  | 'hold-and-delay'
  | 'flank-melee'
  | 'flank-ranged'
  | 'withdraw-with-haste'
  | 'orderly-retreat' // v0M4.0d universal: 10th cohort order added for Orderly-Retreat cascade per §2.8.1.1 amendment.
  | 'fire-at-will'
  | 'aggressive-fire'
  | 'hold-fire';

/**
 * Commander's Order — army-level cascade per `BATTLES.md` §2.8.2
 * (v0M4.0d lock, 2026-07-15+). Sits above cohort orders. On issue,
 * cascades to every cohort on the target side with a template-mapped
 * per-cohort standing order per the cascade table:
 *
 * - `full-assault` → melee `advance-and-attack`, ranged `fire-at-will`.
 * - `stand-ground` → melee `hold-and-defend`, ranged `hold-fire`.
 * - `cautious-assault` → front-row melee `advance-and-attack` + ranged
 *   `fire-at-will`; rear-row melee `hold-and-delay` + ranged `hold-fire`.
 * - `withdraw-with-haste` → all cohorts `withdraw-with-haste`.
 * - `orderly-retreat` → all cohorts `orderly-retreat` (v0M4.0d 10th order).
 *
 * Phase gate: Phase 2 only per v0M4.0d design lock (2026-07-15). Phase 1
 * uses formation picker + reposition; Commander's Orders fire once combat
 * starts. PLAYTEST OPEN: whether to also accept them in Phase 1.
 */
export type CommanderOrder =
  | 'full-assault'
  | 'stand-ground'
  | 'cautious-assault'
  | 'withdraw-with-haste'
  | 'orderly-retreat';

/**
 * Formation template per `BATTLES.md` §2.8.3 (v0M4.0c lock, 2026-07-14+).
 *
 * Player-selectable auto-layout applied during Phase 1 Setup — repositions
 * an entire side's cohorts per the template. All four templates are
 * default-available at game start (no unlock gate — Q7 structure-gate
 * unlocks deferred to v0M4.0k backlog).
 *
 * - **`wide-front`**: cohorts spread across the full battlescape width.
 *   Melee cohorts at centre-front; Ranged on wings; Siege anchoring ends.
 *   Overflow cohorts (>20) push into a second row behind the front.
 * - **`strong-left-flank`**: Melee cohorts concentrated on the left half
 *   (columns 0-9); Ranged + Siege on the right half (10-19). Weight of
 *   decisive attack falls on the left.
 * - **`strong-right-flank`**: mirror of `strong-left-flank`.
 * - **`square`**: defensive perimeter (front / left / right / rear rows
 *   facing outward). Anti-cavalry / anti-flank; reduced coordinated attack.
 */
/**
 * v0M4.3.4 (2026-07-23) — FormationTemplate is now a string alias so
 * the sim can consume the dynamic list of templates loaded from
 * BattleMap.xlsx (currently 'FormationBalanced', 'FormationWideFront').
 * The pre-v0M4.3 hardcoded four-template union is retired.
 */
export type FormationTemplate = string;

/**
 * Persistent move order carried across rounds until fulfilled or cancelled.
 * v0M3.3a first pass: single square target. Cohort steps 1 cardinal
 * square toward target each round (see `phaseMovement` in battle-round.ts).
 * Cleared on arrival at `targetSquare`.
 *
 * Post-v0M3.3a expansions (deferred):
 *  - Multi-square-per-round movement driven by cohort speed (v0M3.3b).
 *  - Terrain-cost movement (v0M3.3c).
 *  - Waypoint chains (Shift+right-click queuing analogue).
 *  - Attack-move (charge into contact then fight).
 */
export interface MoveOrder {
  readonly targetSquare: Square;
}

/** Facing state — reset each battle at deployment per §2.7.8.4.b. */
export interface FacingState {
  /** Current facing direction. */
  direction: Facing;
  /** Whether the cohort has already turned to face an attacker THIS round. §2.7.8.4.b. */
  turnedThisRound: boolean;
}

// ---------------------------------------------------------------------------
// Figure state — per-figure health only (all other stats are cohort-level)
// ---------------------------------------------------------------------------

/**
 * A single figure within a cohort. Per §2.7.8.1.b sub-phase 4:
 *   health ≤ 0 → killed (removed from cohort)
 *   0 < health < 50% of max → wounded (moved to back row)
 *   health ≥ 50% of max → fights on unchanged
 */
export interface Figure {
  /** Current health. Reduced by strikes; can go negative (overkill). */
  health: number;
  /** True if the figure has been wounded and moved off the front row. */
  wounded: boolean;
  /** True if the figure is dead. Dead figures stay in the roster with `health ≤ 0` until cleared. */
  killed: boolean;
}

// ---------------------------------------------------------------------------
// Cohort on the battlescape
// ---------------------------------------------------------------------------

/**
 * A cohort as it exists on the battlescape.
 *
 * The `figures` array is the whole cohort's roster in order:
 *   figures[0..11]   = row 0 (front rank)
 *   figures[12..23]  = row 1
 *   ...
 *   figures[48..59]  = row 4 (back rank)
 * with Siege using only figures[0..3] (4 engines).
 *
 * Rank replacement per §2.7.8.1.b sub-phase 4: when a front-row figure
 * becomes wounded or killed and is removed from the roster's active
 * front, the next available healthy figure from the back rows steps up.
 * The `figures` array is compacted after each strike so `aliveFigures()`
 * always iterates front-first (see cohort.ts helpers).
 */
export interface CohortOnField {
  readonly id: CohortId;
  /**
   * The strategic-map Army this on-field cohort was hydrated from, or
   * null if it was hydrated from a city garrison (v0M4.2.1 per BATTLES.md
   * §3.2). Field battles carry non-null armyIds only. Siege battles
   * (v0M4.2.5) may mix army cohorts (attacker + optional defender field
   * army) with garrison cohorts (armyId null, disambiguated by the
   * hydrated cohort's settlementId reference back on the campaign entity).
   */
  readonly armyId: ArmyId | null;
  readonly factionId: FactionId;
  /**
   * MFix6.2 (2026-07-23) — cohort's Balance.xlsx type name (e.g.
   * "Swordsman", "Garrison", "Archer"). Carried through from the
   * campaign Cohort at hydration so UI labels don't have to
   * tag-sniff (tag-sniffing mislabelled Garrison as Swordsman because
   * both share the Melee tag; Garrison's Charge-Resistance tag was
   * matched by the pre-fix branch to 'spearman' for icon selection
   * but the text label fell through to the Swordsman default).
   *
   * Optional so pre-MFix6.2 test fixtures can construct CohortOnField
   * without setting it; production hydration always populates. UI
   * fallback = the old tag-sniff path when this is undefined.
   */
  readonly type?: string;

  /**
   * Tag set — combat role + abilities. §2.7.8.2.a: at minimum one of
   * Melee / Ranged / Siege / Specialist. Multi-tag allowed (Roman
   * legionary = [Melee, Ranged]). Extended fantasy tags (Flying etc.)
   * live in this array too when post-MVP content lands.
   */
  readonly tags: readonly string[];

  readonly stats: CohortStats;

  /** Current battlescape square (grid position). */
  position: Square;

  /** Current facing + turn-this-round flag. §2.7.8.4.b. */
  facing: FacingState;

  /** Standing order — player-editable per cohort. §2.7.8.1 intro. */
  standingOrder: StandingOrder;

  /**
   * Persistent movement order carried across rounds until reached or
   * cancelled. `null` = holding position (default).
   * See MoveOrder + phaseMovement in battle-round.ts (v0M3.3a).
   */
  moveOrder: MoveOrder | null;

  /**
   * Running cohort-level running values (mutated by strikes/rounds).
   * Reset to start values (100/100 per §2.7.8.11 dial) at battle deploy.
   */
  stamina: number;
  morale: number;

  /**
   * Missile Supply — cohort-level ammo count. Decrements by 1 per FIRING
   * round (§2.7.8.2.b, v0M1.9.10 clarification 2026-07-11 — non-firing
   * rounds cost 0). Only meaningful if tags includes `Ranged`.
   */
  missileSupply: number;

  /** True once the cohort routs (stamina ≤ 0 OR morale ≤ 0). §2.7.8.5.c. */
  broken: boolean;

  /**
   * True once the cohort has exited the battle by reaching a map edge
   * cell under `withdraw-with-haste` (v0M4.0b.2 / §2.8.4). The cohort
   * survives — its state (stamina / morale / figures) still writes back
   * to the campaign Cohort at battle close. But it no longer participates
   * in movement, combat, or checkBattleEnd counts.
   */
  exited: boolean;

  /**
   * Accumulated XP contribution this battle (v0M4.0g). Incremented per
   * strike this cohort lands: +1 per enemy figure wounded this strike,
   * +1 per enemy figure killed this strike (see PLAYTEST OPEN at UNITS.md
   * §5.5 — first pass double-counts a figure that transitions wounded →
   * killed by the same cohort; refine at first playtest if XP inflates).
   * On battle close, battle-conclusion.ts sums this into the cohort's
   * Army's Commander xp field per §5.1-5.2 rank progression.
   */
  xpEarnedThisBattle: number;

  /**
   * Roster of figures. Length = 60 for standard cohorts (12×5) or 4 for
   * Siege. Compacted after each strike so alive+healthy figures front
   * the list per rank replacement (§2.7.8.1.b sub-phase 4).
   */
  figures: Figure[];

  /**
   * Movement bookkeeping for the CURRENT round — reset at start of round.
   *  - `startSquare`: where the cohort was at round start (for direction
   *    check on charge trigger, §2.7.8.3.a).
   *  - `moved`: whether the cohort moved this round.
   *  - `moveDirection`: the cardinal direction of net displacement if
   *    moved this round, else null (for §2.7.8.3.a same-direction rule).
   */
  startSquare: Square;
  movedThisRound: boolean;
  moveDirection: Facing | null;

  /**
   * "Attacked this round" bookkeeping — reset at start of round.
   *  - `struckThisRound`: this cohort was the target of at least one strike.
   *  - `attackedThisRound`: this cohort issued at least one strike.
   */
  struckThisRound: boolean;
  attackedThisRound: boolean;
}

// ---------------------------------------------------------------------------
// Combat log (v0M4.1.1 — BATTLES.md §2.9.1)
// ---------------------------------------------------------------------------

/**
 * Combat-log event catalogue per BATTLES.md §2.9.1 (v0M4.1.1 design lock).
 *
 * Battle carries a battle-scoped array of these events (`Battle.log`),
 * populated at emission points in `battle-round.ts` + `battle-factory.ts`
 * + `battle-conclusion.ts`. Cleared implicitly when the battle is removed
 * from `state.battles` on conclusion.
 *
 * v0M4.1.1 first-pass ships the 7 core event types below. The finer-
 * grain morale-drop / stamina-drop / charge-triggered events (design
 * catalogue §2.9.1) are folded INTO the strike events they follow from
 * (via `chargeActive` flag + kill/wound counts). Extracting them as
 * separate events is a v0M4.1.1+fix candidate if playtest asks for it.
 */
export type CombatLogEvent =
  | { readonly kind: 'melee-strike'; readonly tick: number; readonly round: number; readonly attackerId: CohortId; readonly defenderId: CohortId; readonly hits: number; readonly killCount: number; readonly woundedCount: number; readonly chargeActive: boolean; readonly terrainMultiplier: number }
  | { readonly kind: 'missile-fire'; readonly tick: number; readonly round: number; readonly firerId: CohortId; readonly targetId: CohortId; readonly hits: number; readonly killCount: number; readonly woundedCount: number; readonly friendlyFireHits: number; readonly friendlyFireKills: number; readonly terrainMultiplier: number }
  | { readonly kind: 'rout'; readonly tick: number; readonly round: number; readonly cohortId: CohortId; readonly trigger: 'stamina-zero' | 'morale-zero' | 'commander-broken' | 'cascade' }
  | { readonly kind: 'formation-cascade'; readonly tick: number; readonly round: number; readonly sideIndex: 0 | 1; readonly template: FormationTemplate }
  | { readonly kind: 'commander-order'; readonly tick: number; readonly round: number; readonly sideIndex: 0 | 1; readonly order: CommanderOrder }
  | { readonly kind: 'battle-end'; readonly tick: number; readonly round: number; readonly result: BattleResult }
  | { readonly kind: 'commander-xp'; readonly tick: number; readonly round: number; readonly commanderId: import('./state').CommanderId; readonly xpDelta: number; readonly newLevel: number | null }
  | { readonly kind: 'phase-transition'; readonly tick: number; readonly round: number; readonly fromPhase: BattlePhase | null; readonly toPhase: BattlePhase }
  | { readonly kind: 'wall-strike'; readonly tick: number; readonly round: number; readonly firerId: CohortId; readonly wallCellIndex: number; readonly damage: number; readonly remainingHp: number; readonly breached: boolean };

/**
 * Battle sub-phase — v0M4.1.3. Retires the v0M4.0f atomic round-advance.
 * Each round cycles Movement → Combat → Resolution; Setup is orthogonal
 * (only active during player takeCommand window).
 */
export type BattlePhase = 'setup' | 'movement' | 'combat' | 'resolution';

// ---------------------------------------------------------------------------
// Battle
// ---------------------------------------------------------------------------

/**
 * A single active battle on the strategic map. Created when two Armies
 * of different teams enter ZoC overlap per `MECHANICS.md` §3.2 (v0M3.3
 * scope: constructed manually in tests; ZoC-triggered creation is a
 * follow-up milestone).
 *
 * Two sides — v1 caps at bilateral engagements per §2.7.1 (multi-team
 * ZoC overlap folds into an N-sided engagement in the design but the
 * v0M3.3 first pass supports side count = 2).
 */
export interface Battle {
  readonly id: BattleId;

  /**
   * The two strategic-map Armies participating in this battle (v0M3.4).
   * Order matches `sides`: armyIds[0] is side 0, armyIds[1] is side 1.
   * Cleared to null on the Army's `currentBattleId` when the battle
   * concludes (see `battles.ts`).
   */
  readonly armyIds: readonly [ArmyId, ArmyId];

  /**
   * When `true`, `advanceBattleRound` runs `phaseBattleAI` at the start
   * of every round, which auto-targets cohorts to their nearest enemy
   * and moves melee toward contact / ranged toward missile range (v0M3.8a).
   * This is how ZoC-created battles reach a resolution without the player
   * issuing per-cohort orders. Player-elective "Take command" per §6.1 #13
   * flips this to `false` via the `enter_battle_command` command;
   * `exit_battle_command` flips it back to `true`. Test fixtures set
   * `false` to drive cohort orders explicitly.
   *
   * Not `readonly` — commands mutate this at runtime (v0M3.5).
   */
  autoAI: boolean;

  /**
   * Setup-phase deadline (v0M3.5). Non-null while the battle is in the
   * player-command "Phase 1: Setup" window — `battles.ts` skips
   * `advanceBattleRound` until `state.tick >= phase1EndTick`. Set by
   * `enter_battle_command` to `state.tick + phase1_ticks` (from
   * `battle_phase1_timer_sec` dial × 5 ticks-per-game-day). Cleared to
   * null by `exit_battle_command` or when the deadline passes.
   */
  phase1EndTick: number | null;

  /**
   * Stall-watchdog counter (v0M3.5+fix2). Round index at which the last
   * damage-dealing strike landed. `battle-round.ts` increments this
   * whenever any strike deals ≥1 damage. If `battle.round -
   * lastDamageRound > STALL_LIMIT` (30 rounds), the battle force-
   * concludes as `mutual-withdrawal` to prevent indefinite stalemates
   * from geometry / rules edge cases (Ranged out of ammo, formation
   * lockout, etc.). Set to -1 at battle creation so the first strike
   * of round 0 resets it to 0.
   */
  lastDamageRound: number;

  /**
   * Round-pacing accumulator (v0M4.0f, retired at v0M4.1.3). Superseded
   * by `currentPhase` + `phaseStartTick`; left in the schema for legacy
   * fixtures only. Never read in production sim code — writes are
   * defensive no-ops. Remove after any lingering test fixtures migrate.
   */
  roundTickAccumulator: number;

  /**
   * v0M4.1.3 — Battle sub-phase state per BATTLES.md §2.9.4.
   * `currentPhase` = the phase currently ticking; `phaseStartTick` = the
   * world tick when the current phase started. `battles.ts` fires the
   * phase logic + transitions to the next when the phase's per-phase dial
   * (`battle_phase_movement_ticks` / `..._combat_ticks` / `..._resolution_ticks`)
   * elapses. Retires `round_pacing_ticks`. Movement → Combat → Resolution
   * cycles indefinitely; end of Resolution advances `battle.round`.
   */
  currentPhase: BattlePhase;
  phaseStartTick: number;

  /**
   * Battle-scoped round counter. Increments once per `advanceBattleRound`
   * call. Independent of world tick — see §2.7 Q1 lock (battle clock
   * decoupled from world).
   */
  round: number;

  /**
   * Battle-scoped PRNG. Seeded at battle creation (typically from a
   * derivative of the world seed + battle id). Threaded through every
   * roll (to-hit, target draw, missile scatter) per §2.7.8.13 rule (a).
   */
  rng: RngState;

  /** Cohorts on this side, keyed by cohort id (deterministic iteration via sortedIds). */
  readonly sides: [Side, Side];

  /**
   * Battlefield terrain grid (v0M4.0j — BATTLES.md §2.8.6). One byte
   * per cell, `BATTLESCAPE_CELL_COUNT` entries. Byte value is an ordinal
   * into `TERRAIN_TYPE_ORDER`. Rolled at battle creation, frozen for
   * the battle's lifetime (§2.8.6.7 determinism rule).
   */
  readonly terrain: Uint8Array;

  /**
   * Semantic feature list matching `terrain` (v0M4.0j — BATTLES.md §2.8.6.7).
   * Used by the modal renderer for feature-specific glyphs (bridges,
   * hills clusters, forest patches) and by audit tools. Cells listed
   * here are also encoded in `terrain`; the two sources are consistent
   * by construction of the generator (`battle-terrain.ts`).
   *
   * v0M4.2.4: mutable (was `readonly`) so wall-breach can push a Rubble
   * feature when a Fortification cell converts to Grass.
   */
  terrainFeatures: TerrainFeature[];

  /**
   * Battle-scoped combat log (v0M4.1.1 — BATTLES.md §2.9.1). Emit-only
   * from sim code (`battle-round.ts`, `battle-factory.ts`,
   * `battle-conclusion.ts`); consumed by the modal left-panel log
   * section. Cleared implicitly when the battle is removed from
   * `state.battles` on conclusion.
   *
   * Mutated (not readonly) — sim code pushes new events per round.
   */
  log: CombatLogEvent[];

  /**
   * v0M4.2.3 — non-null iff this is a city-siege battle (BATTLES.md §3.5).
   * Cross-references the `state.settlements` entry the attackers are assaulting.
   * Consumed by:
   *   - `sieges.ts` post-battle: on attacker victory, capture the city.
   *   - `battle-round.ts` phaseBattleAI: Catapult targeting prefers walls
   *     when this is non-null (v0M4.2.4).
   *   - Renderer: siege battles draw the U-shape wall layout instead of
   *     the field-battle terrain scatter.
   * Null for field battles (v0M3.4 flow).
   */
  readonly siegeSettlement: import('./state').SettlementId | null;

  /**
   * v0M7.4d+siege (2026-07-26) — Fort id when this battle is a
   * Fort-assault battle spawned by `createFortAssaultBattleFromArmies`.
   * Peer of `siegeSettlement` (they never both fire in the same
   * battle). Consumed by `battle-conclusion.ts` on attacker victory:
   * flips `WorldStructure.ownerFactionId` + destroys the garrison
   * cohorts. Optional/back-compat: undefined for field battles + for
   * pre-v0M7.4d+siege fixtures.
   */
  readonly siegeFortId?: import('./state').WorldStructureId | null;

  /**
   * v0M4.2.3 — HP per wall cell in a siege battle (BATTLES.md §3.6, §3.7).
   * Key = terrain cell index (`y * BATTLESCAPE_GRID + x`). Value = current
   * HP in [0, wall_hp dial]. Empty for field battles. Entries exist ONLY
   * for Fortification cells generated as walls by
   * `generateSiegeBattleTerrain`; battlefield-scatter Fortification cells
   * (§2.8.6 field-battle placement) never have wallHp entries and are
   * treated as regular cover terrain.
   *
   * v0M4.2.4 makes this damageable: Ranged+Siege attacks decrement,
   * HP=0 → cell converts to Grass rubble. v0M4.2.3 only populates
   * initial values.
   */
  readonly wallHp: Map<number, number>;

  /**
   * Battle result once concluded — null while active.
   *  - 'side0-victory': all side-1 cohorts routed
   *  - 'side1-victory': all side-0 cohorts routed
   *  - 'mutual-withdrawal': both sides broken; hit `duration_cap` or
   *    equivalent termination (v0M3.3 first pass: not implemented; battles
   *    concluding are single-victory only).
   */
  result: BattleResult | null;
}

export type BattleResult = 'side0-victory' | 'side1-victory' | 'mutual-withdrawal';

export interface Side {
  readonly sideIndex: 0 | 1;
  /** Cohorts on this side, keyed by cohort id. Deterministic iteration via sortedIds(). */
  readonly cohorts: Map<CohortId, CohortOnField>;
  /**
   * Commander of the Army this side represents. In v1 (Default commander
   * per §2.7.8.7.c), this reference is present but grants no bonuses —
   * kept so the Hero commander hook (post-MVP) can drop in without
   * schema change.
   */
  readonly commanderId: CommanderId;
}
